import { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";

// Floating "Install Filmistani" prompt — shows once per device, dismissible for 30 days.
// On Android/Desktop: uses native beforeinstallprompt event.
// On iOS Safari: no native API — we show instructions ("Tap Share → Add to Home Screen").

const DISMISS_KEY = "fm_pwa_dismissed_at";
const DISMISS_DAYS = 30;

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
}

function recentlyDismissed() {
  const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
  if (!ts) return false;
  const ageDays = (Date.now() - ts) / (1000 * 60 * 60 * 24);
  return ageDays < DISMISS_DAYS;
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosVariant, setIosVariant] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    // Android / Desktop Chrome flow
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setTimeout(() => setShow(true), 8000); // wait 8s so users browse first
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS Safari has no native install API → show our own hint after a delay
    if (isIOS()) {
      const t = setTimeout(() => {
        setIosVariant(true);
        setShow(true);
      }, 10000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      };
    }

    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch { /* ignore */ }
    setDeferred(null);
    dismiss();
  };

  if (!show) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm z-[200] bg-[#0F0F0F] border border-white/10 rounded-lg shadow-2xl p-4 fm-mobile-drawer"
    >
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        data-testid="pwa-dismiss-btn"
        className="absolute top-2 right-2 text-neutral-500 hover:text-white p-1"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <div className="shrink-0 w-10 h-10 rounded-lg bg-black border border-[#E50914] flex items-center justify-center text-white font-black text-xl">F</div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-base font-extrabold tracking-tight">
            {iosVariant ? "Add Filmistani to Home Screen" : "Install Filmistani"}
          </h3>
          {iosVariant ? (
            <p className="text-xs text-neutral-400 mt-1 leading-relaxed">
              Fullscreen launch, no Safari bar, offline browsing — feels like a native app.
            </p>
          ) : (
            <p className="text-xs text-neutral-400 mt-1 leading-relaxed">
              Fullscreen, instant launch, and offline browsing. One tap to install — no app store.
            </p>
          )}
        </div>
      </div>
      {iosVariant ? (
        <>
          <ol className="mt-3 space-y-2 text-xs text-neutral-300">
            <li className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-[#E50914] text-white text-[10px] font-bold flex items-center justify-center shrink-0">1</span>
              <span>Tap the <Share className="inline w-3.5 h-3.5 -mt-0.5 text-[#007AFF]" /> <strong className="text-white">Share</strong> button in Safari's bottom bar</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-[#E50914] text-white text-[10px] font-bold flex items-center justify-center shrink-0">2</span>
              <span>Choose <strong className="text-white">Add to Home Screen</strong></span>
            </li>
          </ol>
          <button
            onClick={dismiss}
            data-testid="pwa-ios-gotit-btn"
            className="mt-3 w-full fm-btn-ghost px-4 py-2 rounded-md font-semibold text-sm"
          >
            Got it
          </button>
        </>
      ) : (
        <div className="mt-3 flex gap-2">
          <button
            onClick={install}
            data-testid="pwa-install-btn"
            className="flex-1 fm-btn-red px-4 py-2 rounded-md font-semibold text-sm flex items-center justify-center gap-2"
          >
            <Download className="w-4 h-4" /> Install
          </button>
          <button
            onClick={dismiss}
            data-testid="pwa-later-btn"
            className="px-4 py-2 rounded-md font-medium text-sm text-neutral-400 hover:text-white border border-white/10"
          >
            Later
          </button>
        </div>
      )}
    </div>
  );
}
